/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { maxSatisfying } from "semver";
import { Package, PackageVersionRegistry, ResolvedPackage } from "types/packageTypes";
import { ImportMap } from "@sandbox/transpiler";
import { latestPackageSet } from "./LatestPackageSet";

export type iTwinjsMajorVersion = "2.x" | "3.x";

export const packageService = {
  /** Gets major version of itwinjs core family of packages, or undefined if itwinjs is not used  */
  getItwinjsVersion: (resolvedPackages: ResolvedPackage[]): iTwinjsMajorVersion | undefined => {
    const pkg = resolvedPackages.find((item) => item.itwinCore);

    if (!pkg)
      return undefined;

    if (pkg.version.startsWith("2"))
      return "2.x";

    if (pkg.version.startsWith("3"))
      return "3.x";
  },

  getGlobals: (resolvedPackages: ResolvedPackage[]) => {
    return resolvedPackages.filter((pkg) => pkg.type === "cjs" && pkg.globalName);
  },

  /** get distinct set of type declaration urls (Legacy itwinjs 2.x has the same types file for every package) */
  getTypeDefinitions: (resolvedPackages: ResolvedPackage[]) => {
    const dict: Record<string, boolean> = {};
    for (const pkg of resolvedPackages) {
      if (pkg.typeDefinitionUrl)
        dict[pkg.typeDefinitionUrl] = true;
    }
    return Object.keys(dict);
  },

  getImportmapPaths: (resolvedPackages: ResolvedPackage[]): ImportMap => {
    const imports: Record<string, string> = {};
    for (const item of resolvedPackages.filter((pkg) => pkg.type === "esm")) {
      imports[item.name] = item.path;
    }

    return {
      imports
    };
  },

  getScriptPaths: (resolvedPackages: ResolvedPackage[]) => {
    return resolvedPackages.filter((pkg) => pkg.type === "cjs").map((pkg) => pkg.path);
  },

  /** Return a new array with alphabetically sorted packages by name */
  sortPackages: (packages: ResolvedPackage[] | Package[]) => {
    const sortedPackages = [...packages];
    sortedPackages.sort((a, b) => {
      const nameA = a.name.toUpperCase(); // ignore upper and lowercase
      const nameB = b.name.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });
    return sortedPackages;
  },

  getUpdatedPackages: (packageVersionRegistry: PackageVersionRegistry, packages: Package[], resolvedPackages: ResolvedPackage[]) => {
    const updatedPackages: Package[] = [];
    for (const pkg of packages) {
      if (latestPackageSet[pkg.name] && packageVersionRegistry[pkg.name]) {
        const version = maxSatisfying(packageVersionRegistry[pkg.name], latestPackageSet[pkg.name]);
        const resolvedPkg = resolvedPackages.find((item) => item.name === pkg.name);
        if (version === null)
          throw new Error(`Unexpected error, failed to find maxSatisfying version for ${pkg.name}@${latestPackageSet[pkg.name]}`);
        if (!resolvedPkg)
          throw new Error(`Unexpected error, package ${pkg.name} was not found in the list of resolved packages`);

        if (version !== resolvedPkg.version) {
          updatedPackages.push({
            name: pkg.name,
            version,
          });
        }
      }
    }
    return updatedPackages;
  },
};
